import React, { useState, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { Grid } from '@/components/common/grid';
import useAttachmentOcrResults, {
  OCRResult,
} from 'src/api/core/inbound-emails/useAttachmentOcrResults';
import { EmailAttachment } from './email-viewer-types';

interface OCRResultsTabProps {
  inboundEmailId?: string;
  attachments?: EmailAttachment[];
  onRefreshData?: () => void;
}

// Component to render JSON in an expandable, interactive way
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];
type JsonValue =
  string | number | boolean | null | undefined | JsonObject | JsonArray;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // eslint-disable-next-line i18next/no-literal-string
  if (data === null) return <span style={{ color: '#999' }}>null</span>;
  if (data === undefined)
    // eslint-disable-next-line i18next/no-literal-string
    return <span style={{ color: '#999' }}>undefined</span>;

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return <span style={{ color: '#009688' }}>&quot;{data}&quot;</span>;
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        // eslint-disable-next-line i18next/no-literal-string
        <span style={{ color: '#ff9800' }}>{data ? 'true' : 'false'}</span>
      );
    }
    return <span>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      // eslint-disable-next-line i18next/no-literal-string
      return <span>[ ]</span>;
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0 }}>
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {expanded.root ? '▼' : '▶'} Array [{data.length}]
        </span>

        {expanded.root && (
          <div style={{ paddingLeft: 20 }}>
            {data.map((item, i) => (
              <div key={i}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0 }}>
      <span
        onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {expanded.root ? '▼' : '▶'} Object{' '}
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}
      </span>

      {expanded.root && (
        <div style={{ paddingLeft: 20 }}>
          {keys.map((key) => (
            <div key={key}>
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OCRResultsTab: React.FC<OCRResultsTabProps> = ({
  inboundEmailId,
  attachments,
  onRefreshData,
}) => {
  const gt = useGT();
  const [selectedAttachmentForOCR, setSelectedAttachmentForOCR] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState(0);
  const hasAttachments = !!attachments?.length;

  const {
    data: ocrData,
    isLoading: isLoadingOCR,
    error: ocrError,
  } = useAttachmentOcrResults(
    inboundEmailId || '',
    selectedAttachmentForOCR || '',
    !!(selectedAttachmentForOCR && inboundEmailId),
  );

  // Debug logging
  useEffect(() => {
    if (ocrData) {
      console.log('OCR Data received:', ocrData);
      console.log('OCR Results array:', ocrData?.ocrResults);
    }
    if (ocrError) {
      console.error('OCR Error:', ocrError);
    }
  }, [ocrData, ocrError]);

  // Find the selected attachment object
  const selectedAttachment = attachments?.find(
    (attachment) => attachment.id === selectedAttachmentForOCR,
  );

  // Parse extractedText[0] as JSON if it exists
  const parseExtractedText = (result: OCRResult) => {
    if (!result?.extractedText || !result.extractedText.length) {
      return null;
    }

    try {
      return JSON.parse(result.extractedText[0]);
    } catch (e) {
      console.error('Failed to parse extractedText as JSON:', e);
      return null;
    }
  };

  if (!hasAttachments) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          <T>No attachments found</T>
        </MDTypography>
      </MDBox>
    );
  }

  const renderAttachmentPreview = () => {
    if (!selectedAttachment) return null;

    const isPDF =
      selectedAttachment.mimeType === 'application/pdf' ||
      selectedAttachment.fileName?.toLowerCase().endsWith('.pdf');
    const isImage = selectedAttachment.mimeType?.startsWith('image/');

    if (isPDF) {
      return (
        <MDBox mb={3}>
          <MDTypography variant="subtitle2" mb={2}>
            <T>Document Preview</T>
          </MDTypography>
          <MDBox
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
            overflow="hidden"
            height="500px"
          >
            <PDFViewer
              url={selectedAttachment.s3Url}
              pageMode="all-pages"
              onUrlExpired={onRefreshData}
            />
          </MDBox>
        </MDBox>
      );
    }

    if (isImage) {
      return (
        <MDBox mb={3}>
          <MDTypography variant="subtitle2" mb={2}>
            <T>Image Preview</T>
          </MDTypography>
          <MDBox
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
            p={1}
            textAlign="center"
          >
            <MDBox
              component="img"
              src={selectedAttachment.s3Url}
              alt={selectedAttachment.fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
            />
          </MDBox>
        </MDBox>
      );
    }

    return (
      <MDBox mb={3}>
        <MDTypography variant="subtitle2" mb={2}>
          <T>File Preview</T>
        </MDTypography>
        <MDBox
          p={3}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="borderRadius"
          textAlign="center"
        >
          <MDTypography variant="body2" color="text.secondary">
            <T>
              Preview not available for file type:{' '}
              <Var>
                {selectedAttachment.mimeType ||
                  gt('Unknown', { context: 'unknown file type fallback' })}
              </Var>
            </T>
          </MDTypography>
          <MDTypography
            component="a"
            href={selectedAttachment.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            color="info"
            sx={{ display: 'block', mt: 2 }}
          >
            <T context="link to download a file">Download File</T>
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <MDBox p={2}>
      <MDBox mb={3}>
        <MDTypography variant="subtitle2" mb={2}>
          <T>Select an attachment to view OCR results:</T>
        </MDTypography>
        <Grid container spacing={2}>
          {attachments?.map((attachment) => (
            <Grid item xs={12} md={6} lg={4} key={attachment.id}>
              <MDBox
                p={2}
                border="1px solid"
                borderColor={
                  selectedAttachmentForOCR === attachment.id
                    ? 'info.main'
                    : 'grey.300'
                }
                borderRadius="borderRadius"
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'info.main',
                    backgroundColor: 'rgba(0, 127, 255, 0.05)',
                  },
                }}
                onClick={() => setSelectedAttachmentForOCR(attachment.id)}
              >
                <MDTypography variant="body2" fontWeight="medium">
                  {attachment.fileName}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="file size and type label">
                    <Var>{(attachment.sizeBytes / 1024).toFixed(2)}</Var> KB •{' '}
                    <Var>
                      {attachment.mimeType ||
                        gt('Unknown type', {
                          context: 'fallback when file mime type is unknown',
                        })}
                    </Var>
                  </T>
                </MDTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>

      {selectedAttachmentForOCR && (
        <>
          {renderAttachmentPreview()}

          <MDBox
            mt={3}
            p={3}
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
          >
            <MDTypography variant="subtitle2" mb={2}>
              <T>OCR Results</T>
            </MDTypography>

            {isLoadingOCR ? (
              <MDBox display="flex" justifyContent="center" my={3}>
                <Spinner />
              </MDBox>
            ) : (
              <>
                {ocrData?.ocrResults?.length ? (
                  ocrData.ocrResults.map((result) => {
                    const parsedExtractedText = parseExtractedText(result);

                    return (
                      <MDBox key={result.id} mb={3}>
                        <MDBox mb={2}>
                          <MDTypography variant="body2" fontWeight="medium">
                            <T>
                              Provider: <Var>{result.vendor}</Var> (Model:{' '}
                              <Var>{result.modelVersion}</Var>)
                            </T>
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            <T>
                              Status: <Var>{result.status}</Var> - Created:{' '}
                              <Var>
                                {new Date(result.createdAt).toLocaleString()}
                              </Var>
                            </T>
                          </MDTypography>
                        </MDBox>

                        {result.error ? (
                          <MDBox
                            p={2}
                            bgcolor="error.light"
                            borderRadius="borderRadius"
                            color="error.dark"
                          >
                            <MDTypography variant="body2">
                              {result.error}
                            </MDTypography>
                          </MDBox>
                        ) : (
                          <MDBox>
                            <Tabs
                              value={String(activeTab)}
                              onValueChange={(value) =>
                                setActiveTab(Number(value))
                              }
                              className="mb-4"
                            >
                              <TabsList>
                                <TabsTrigger value="0">
                                  {gt('Parsed JSON', {
                                    context: 'tab label for parsed JSON view',
                                  })}
                                </TabsTrigger>
                                <TabsTrigger value="1">
                                  {gt('Raw Results', {
                                    context:
                                      'tab label for raw OCR results view',
                                  })}
                                </TabsTrigger>
                              </TabsList>
                            </Tabs>

                            {activeTab === 0 && (
                              <MDBox
                                p={2}
                                bgcolor="grey.50"
                                borderRadius="borderRadius"
                                sx={{
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: '14px',
                                }}
                              >
                                <MDBox
                                  mb={2}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {parsedExtractedText ? (
                                      <T context="label showing the parsed JSON section">
                                        Extracted Text JSON (Parsed)
                                      </T>
                                    ) : (
                                      <T context="label when no valid JSON was found">
                                        No Valid JSON Found
                                      </T>
                                    )}
                                  </MDTypography>
                                  {parsedExtractedText && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        if (!result.extractedText) return;
                                        navigator.clipboard.writeText(
                                          result.extractedText[0],
                                        );
                                      }}
                                    >
                                      <T context="button label to copy JSON to clipboard">
                                        Copy JSON String
                                      </T>
                                    </Button>
                                  )}
                                </MDBox>

                                {parsedExtractedText ? (
                                  <JsonViewer
                                    data={
                                      parsedExtractedText as unknown as JsonValue
                                    }
                                  />
                                ) : (
                                  <MDTypography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <T>
                                      No valid JSON found in extractedText[0] or
                                      extractedText is empty.
                                    </T>
                                    {result.extractedText &&
                                      result.extractedText.length > 0 && (
                                        <MDBox mt={2}>
                                          <MDTypography
                                            variant="body2"
                                            component="div"
                                          >
                                            <T context="label for raw content display">
                                              Raw content of extractedText[0]:
                                            </T>
                                          </MDTypography>
                                          <MDBox
                                            mt={1}
                                            p={2}
                                            bgcolor="grey.100"
                                            borderRadius="4px"
                                            sx={{
                                              whiteSpace: 'pre-wrap',
                                              overflowWrap: 'break-word',
                                              maxHeight: '200px',
                                              overflowY: 'auto',
                                            }}
                                          >
                                            {result.extractedText[0]}
                                          </MDBox>
                                        </MDBox>
                                      )}
                                  </MDTypography>
                                )}
                              </MDBox>
                            )}

                            {activeTab === 1 && (
                              <MDBox
                                p={2}
                                bgcolor="grey.50"
                                borderRadius="borderRadius"
                                sx={{
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: '14px',
                                }}
                              >
                                <MDBox
                                  mb={2}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    <T>Complete OCR Result</T>
                                  </MDTypography>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const jsonStr = JSON.stringify(
                                        result,
                                        null,
                                        2,
                                      );
                                      navigator.clipboard.writeText(jsonStr);
                                    }}
                                  >
                                    <T context="button label to copy full JSON to clipboard">
                                      Copy Full JSON
                                    </T>
                                  </Button>
                                </MDBox>

                                <JsonViewer
                                  data={result as unknown as JsonValue}
                                />
                              </MDBox>
                            )}
                          </MDBox>
                        )}
                      </MDBox>
                    );
                  })
                ) : (
                  <MDTypography variant="body2" color="text.secondary">
                    <T>No OCR results found for this attachment</T>
                  </MDTypography>
                )}
              </>
            )}
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default OCRResultsTab;
